import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import { ApiResponse } from '../../types/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import { ChecklistGroup } from '../../types';
import ImportModal from '../../components/Common/ImportModal';
import { gruposChecklistImportConfig } from '../../config/importConfigs';

const ChecklistGroups: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: groups,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<ChecklistGroup>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getChecklistGroups({
          page,
          limit,
          tipo: filters?.tipo || undefined,
          status: filters?.status || undefined,
          search: filters?.search || undefined
        });

        if (response.success && response.data) {
          const grupos = response.data.grupos || [];

          return {
            success: true,
            data: {
              items: grupos,
              pagination: {
                total: response.data.pagination?.total || grupos.length
              }
            }
          };
        }

        throw new Error('Falha ao carregar grupos de checklist');
      } catch (error) {
        console.error('Erro ao carregar grupos de checklist:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar grupos de checklist'
        };
      }
    },
    itemsPerPage: 10,
    filters: { tipo: typeFilter, status: statusFilter, search: filter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    // Normaliza o status para lowercase para comparação
    const statusLower = status.toLowerCase();

    const statusMap: Record<string, string> = {
      'ativo': 'status-active',
      'inativo': 'status-inactive'
    };
    return statusMap[statusLower] || statusMap[status] || 'status-pending';
  };

  const getObrigatorioPercentage = (group: ChecklistGroup): number => {
    if (group.quantidade_itens === 0) return 0;
    return Math.round((group.itens_obrigatorios / group.quantidade_itens) * 100);
  };

  const getObrigatorioClass = (percentage: number): string => {
    if (percentage >= 80) return 'status-success';
    if (percentage >= 50) return 'status-warning';
    return 'status-danger';
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.nome.toLowerCase().includes(filter.toLowerCase()) ||
                         (group.descricao && group.descricao.toLowerCase().includes(filter.toLowerCase())) ||
                         (group.categoria && group.categoria.toLowerCase().includes(filter.toLowerCase()));
    const matchesType = !typeFilter || group.tipo === typeFilter;
    const matchesStatus = !statusFilter || group.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const viewGroup = (id: number) => {
    navigate(`/grupos-checklist/visualizar/${id}`);
  };

  const editGroup = (id: number) => {
    navigate(`/grupos-checklist/editar/${id}`);
  };

  const deleteGroup = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        const response = await apiService.deleteChecklistGroup(id) as ApiResponse<{ message: string }>;
        if (response.success) {
          utils.showNotification('Grupo excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erro ao excluir grupo');
        }
      } catch (error) {
        console.error('Erro ao excluir grupo:', error);
        utils.showNotification('Erro ao excluir grupo', { type: 'error' });
      }
    }
  };

  const getTypes = () => {
    const types = Array.from(new Set(groups.map(g => g.tipo).filter((t): t is string => t !== null)));
    return types;
  };


  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Grupos de Checklist</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Grupo
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando grupos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Grupos de Checklist</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.GRUPOS_CHECKLIST) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.GRUPOS_CHECKLIST) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/grupos-checklist/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Grupo
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Gerenciamento de Grupos</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Nome, descrição ou categoria...'
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getTypes().map(type => ({ value: type, label: type }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'Ativo', label: 'Ativo' },
                    { value: 'Inativo', label: 'Inativo' }
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setTypeFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Grupos"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredGroups.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="grupos de checklist"
              />
            )}

            {filteredGroups.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-layer-group empty-state-icon"></i>
                <h3>Nenhum grupo encontrado</h3>
                <p>{filter || typeFilter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros grupos de checklist.'}</p>
                {canCreate(SystemModule.GRUPOS_CHECKLIST) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/grupos-checklist/novo')}
                  >
                    Cadastrar Primeiro Grupo
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Ordem</th>
                      <th>Nome do Grupo</th>
                      <th>Descrição</th>
                      <th>Tipo</th>
                      <th>Categoria</th>
                      <th>Itens</th>
                      <th>Obrigatórios</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGroups.map(group => {
                      const obrigatorioPercentage = getObrigatorioPercentage(group);
                      return (
                        <tr key={group.id}>
                          <td>
                            <span className="badge badge-secondary">
                              {group.ordem}
                            </span>
                          </td>
                          <td>
                            <strong>{group.nome}</strong>
                          </td>
                          <td>
                            <div style={{ maxWidth: '200px' }}>
                              {group.descricao}
                            </div>
                          </td>
                          <td>
                            <span className="status-badge status-info">
                              {group.tipo}
                            </span>
                          </td>
                          <td>{group.categoria || '-'}</td>
                          <td>
                            {group.quantidade_itens > 0 ? (
                              <span className="badge badge-light">
                                {group.quantidade_itens} itens
                              </span>
                            ) : (
                              <span className="status-badge status-danger" style={{ fontSize: '0.75rem' }}>
                                0 itens
                              </span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className={`status-badge ${getObrigatorioClass(obrigatorioPercentage)}`}>
                                {obrigatorioPercentage}%
                              </span>
                              <small style={{ color: '#7f8c8d' }}>
                                ({group.itens_obrigatorios}/{group.quantidade_itens})
                              </small>
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusClass(group.status)}`}>
                              {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-view"
                                onClick={() => viewGroup(group.id)}
                                title="Visualizar"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              {canUpdate(SystemModule.GRUPOS_CHECKLIST) && (
                                <button
                                  className="btn-edit"
                                  onClick={() => editGroup(group.id)}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              )}
                              {canDelete(SystemModule.GRUPOS_CHECKLIST) && (
                                <button
                                  className="btn-delete"
                                  onClick={() => deleteGroup(group.id)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos grupos */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-layer-group"></i>
            </div>
            <div className="stat-value">
              {groups.length}
            </div>
            <div className="stat-label">Total de Grupos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {groups.filter(g => g.status === 'Ativo').length}
            </div>
            <div className="stat-label">Grupos Ativos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-list-check"></i>
            </div>
            <div className="stat-value">
              {groups.reduce((total, group) => total + group.quantidade_itens, 0)}
            </div>
            <div className="stat-label">Total de Itens</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div className="stat-value">
              {groups.reduce((total, group) => total + group.itens_obrigatorios, 0)}
            </div>
            <div className="stat-label">Itens Obrigatórios</div>
          </div>
        </div>

        {/* Distribuição por tipo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Tipo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getTypes().map(type => {
                const typeGroups = groups.filter(g => g.tipo === type);
                const totalItems = typeGroups.reduce((total, group) => total + group.quantidade_itens, 0);
                const totalObrigatorios = typeGroups.reduce((total, group) => total + group.itens_obrigatorios, 0);
                const percentage = Math.round((typeGroups.length / groups.length) * 100);
                
                return (
                  <div key={type} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{type}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{typeGroups.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Total de itens: <strong>{totalItems}</strong></div>
                      <div>Itens obrigatórios: <strong>{totalObrigatorios}</strong></div>
                      <div>Grupos ativos: <strong>{typeGroups.filter(g => g.status === 'Ativo').length}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Ordem dos grupos */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Ordem de Execução</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {groups
                .filter(g => g.status === 'Ativo')
                .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                .map(group => (
                  <div key={group.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '6px',
                    borderLeft: '4px solid #04C64F'
                  }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      background: '#04C64F',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      marginRight: '12px'
                    }}>
                      {group.ordem}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {group.nome}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        {group.quantidade_itens} itens ({group.itens_obrigatorios} obrigatórios)
                      </div>
                    </div>
                    <div style={{ color: '#04C64F', fontWeight: 'bold' }}>
                      {getObrigatorioPercentage(group)}%
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={gruposChecklistImportConfig}
      />
    </div>
  );
};

export default ChecklistGroups;
